import * as React from 'react';

import { enumCurrencyCode } from '@rematter/frontend-lib-api-client';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $currentDraftLineItems,
  $effectiveLineItems,
  $remoteBillDetail,
} from '../../_state/billDetail.state';
import { BillRecord } from '../../types';
import { FinanceDetailLayout } from '../_layouts/FinanceDetailLayout';
import { FinanceLineItemTable } from '../_shared/FinanceLineItemTable/FinanceLineItemTable';
import { FinanceLineItemData } from '../_shared/FinanceLineItemTable/types';

import { BillDetailFooter } from './BillDetailFooter';

type Props = {
  disabled?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ BILL TAB LINE ITEMS ⚛️ ⚛️ ⚛️
//
export const BillTabLineItems: React.FC<Props> = ({ disabled = false }) => {
  // Atoms
  const effectiveLineItems = useAtomValue($effectiveLineItems);
  const setDraftLineItems = useSetAtom($currentDraftLineItems);
  const queryResult = useAtomValue($remoteBillDetail);
  const bill = (queryResult?.data as { bill: BillRecord | null } | undefined)
    ?.bill;

  const currencyCode = bill?.amount?.currencyCode ?? enumCurrencyCode.USD;

  // Callbacks
  const handleUpdateLineItem = React.useCallback(
    (id: string, updates: Partial<FinanceLineItemData>) => {
      setDraftLineItems(
        effectiveLineItems.map((item) =>
          item.id === id ? { ...item, ...updates } : item,
        ),
      );
    },
    [effectiveLineItems, setDraftLineItems],
  );

  const handleDeleteLineItem = React.useCallback(
    (id: string) => {
      setDraftLineItems(effectiveLineItems.filter((item) => item.id !== id));
    },
    [effectiveLineItems, setDraftLineItems],
  );

  const handleAddLineItem = React.useCallback(() => {
    const maxSortOrder = effectiveLineItems.reduce(
      (max, item) => Math.max(max, item.sortOrder),
      -1,
    );
    const newItem: FinanceLineItemData = {
      id: `temp-${Date.now()}-${Math.random()}`,
      description: '',
      quantity: null,
      unit: null,
      unitPrice: null,
      totalPrice: null,
      itemId: null,
      groupName: null,
      sortOrder: maxSortOrder + 1,
    };
    setDraftLineItems([...effectiveLineItems, newItem]);
  }, [effectiveLineItems, setDraftLineItems]);

  return (
    <FinanceDetailLayout
      className="@BillTabLineItems h-auto"
      footer={<BillDetailFooter />}
    >
      <FinanceLineItemTable
        lineItems={effectiveLineItems}
        onUpdateLineItem={handleUpdateLineItem}
        onDeleteLineItem={handleDeleteLineItem}
        onAddLineItem={handleAddLineItem}
        currencyCode={currencyCode}
        disabled={disabled}
      />
    </FinanceDetailLayout>
  );
};
